import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { GRAPH_HEIGHT } from './index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import isEqual from 'lodash/isEqual';
import { NonBasicCardStyling } from './util';
import { useBuildoutOsDisplay } from 'components/buildout_os_display';
import FilterSelect from './filter_select';
import FilterButton from 'components/ui/filter_button';
import FilterCheckboxGroup from 'components/search_sidebar/filter_checkbox_group';

export type Analytics = {
  bounceRate: number,
  clickRate: number,
  id: number,
  name: string,
  numBounces: number,
  numClicks: number,
  numDelivered: number,
  numUniqueOpens: number,
  openRate: number,
  sentAt: string,
  subject: string,
  unsubscribeCount: number,
  unsubscribeRate: number
}

export type EmailAnalyticsProps = {
  emailAnalytics: Analytics[],
  filters?: {
    selectedEmails?: number[]
  },
  onFilterUpdate?: (filters: any) => void,
  preview: boolean,
}

const DELIVERIES_COLOR = '#203D88';
const OPENS_COLOR = '#2153D4';
const CLICKS_COLOR = '#3F86F2';
// Remove unsubscribe metric for now, might be added back later after beta
// const UNSUBSCRIBES_COLOR = '#95C7FB';
const BACKGROUND_COLOR = '#fff';

export default function EmailAnalytics({
  emailAnalytics,
  filters = {},
  onFilterUpdate,
  preview = false,
}: EmailAnalyticsProps) {
  const containerRef = useRef(null);
  const buildoutOs = useBuildoutOsDisplay();
  const [emailsSelected, setEmailsSelected] = useState<number[]>(
    filters.selectedEmails || []
  );

  const updateFilters = useCallback((filterUpdates) => {
    if (!preview && onFilterUpdate) {
      onFilterUpdate(filterUpdates);
    }
  }, [preview, onFilterUpdate]);

  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    }, [value]);
    return ref.current;
  }

  const prevFilters = usePrevious(filters);

  useEffect(() => {
    if (prevFilters && !isEqual(prevFilters, filters)) {
      if (filters.selectedEmails) {
        setEmailsSelected(filters.selectedEmails);
      }
    }
  }, [filters, prevFilters]);

  const emailsToUse = emailsSelected.length === 0 ? emailAnalytics :
    emailAnalytics.filter(email => emailsSelected.includes(email.id));

  const totalEmailsSent = emailsToUse.reduce((sum, email) => sum + email.numDelivered, 0);
  const totalOpens = emailsToUse.reduce((sum, email) => sum + email.numUniqueOpens, 0);
  const totalClicks = emailsToUse.reduce((sum, email) => sum + email.numClicks, 0);
  // Remove unsubscribe metric for now, might be added back later after beta
  // const totalUnsubscribes = emailsToUse.reduce((sum, email) => sum + email.optOuts, 0);

  const emailOptions = emailAnalytics.map(email => ({ value: email.id, label: email.name, id: email.id.toString() }));

  const emailOptionsWithAll = [
    { id: 'all-emails', label: 'All Emails', value: 'all-emails' },
    ...emailOptions
  ];

  const allEmailsSelected =
    (emailsSelected.length > 0 && emailsSelected.length === emailOptions.length) || emailOptions.length === 0;

  const emailsFiltered = emailsSelected.length > 0 && !allEmailsSelected;
  let osEmailLabel = 'All Emails';
  if (emailsFiltered && emailsSelected.length === 1) {
    osEmailLabel = emailAnalytics.find(email => email.id === emailsSelected[0])?.name ?? '1 email';
  }
  if (emailsFiltered && emailsSelected.length > 1) osEmailLabel = `${emailsSelected.length} emails`;

  const handleEmailFilterChange = useCallback((e) => {
    const { checked, value } = e.target;

    if (value === 'all-emails') {
      const newSelectedEmails = checked ? emailOptions.map(option => option.value) : [];
      setEmailsSelected(newSelectedEmails);
      updateFilters({ selectedEmails: newSelectedEmails });
      return;
    }

    const newSelectedEmails = checked
      ? [...emailsSelected, Number(value)]
      : emailsSelected.filter(id => id !== Number(value));

    setEmailsSelected(newSelectedEmails);
    updateFilters({ selectedEmails: newSelectedEmails });
  }, [emailsSelected, updateFilters]);

  return (
    <div className={NonBasicCardStyling} ref={containerRef}>
      <div className="align-items-center d-flex justify-content-between mt-2">
        <h6 className="m-0 fw-normal">Email Analytics</h6>
        <div className="d-flex align-items-center" style={{ width: '40%' }}>
          {buildoutOs ? (
            <FilterSelect
              ariaLabel="Emails"
              label={osEmailLabel}
              options={emailOptionsWithAll}
              selected={allEmailsSelected ? [...emailsSelected, 'all-emails'] : emailsSelected}
              onChange={handleEmailFilterChange}
            />
          ) : (
            <FilterButton
              container={containerRef.current}
              containerClasses="flex-grow-1 mr-3"
              filtersApplied={emailsSelected.length > 0}
              label="All Emails"
              name="emailsFilter"
            >
              <FilterCheckboxGroup
                options={emailOptionsWithAll}
                selected={allEmailsSelected ? [...emailsSelected, 'all-emails'] : emailsSelected}
                onChange={handleEmailFilterChange}
              />
            </FilterButton>
          )}
        </div>
      </div>

      <div className="d-flex flex-column justify-content-center align-items-center gap-4">
        <div className="position-relative" style={{ height: `${GRAPH_HEIGHT}px` }}>
          <Doughnut
            data={{
              datasets: [{
                backgroundColor: [DELIVERIES_COLOR],
                borderWidth: 0,
                data: [totalEmailsSent],
                label: 'deliveries'
              }, {
                backgroundColor: [OPENS_COLOR, BACKGROUND_COLOR],
                borderWidth: 0,
                data: [totalOpens, totalEmailsSent - totalOpens],
                label: 'opens'
              }, {
                backgroundColor: [CLICKS_COLOR, BACKGROUND_COLOR],
                borderWidth: 0,
                data: [totalClicks, totalEmailsSent - totalClicks],
                label: 'clicks'
              }
              // Remove unsubscribe metric for now, might be added back later after beta
              // {
              //   backgroundColor: [UNSUBSCRIBES_COLOR, BACKGROUND_COLOR],
              //   borderWidth: 0,
              //   data: [totalUnsubscribes, totalEmailsSent - totalUnsubscribes],
              //   label: 'Unsubscribes'
              // }
              ]
            }}
            options={{
              aspectRatio: 1,
              maintainAspectRatio: false,
              cutoutPercentage: 65,
              responsive: true,
              hover: { mode: null },
              tooltips: { enabled: false }
            }}
          />
          <div
            className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center flex-column gap-2"
            style={{ top: '0', left: '0' }}
          >
            <h3 className="m-0">
              <strong>{totalEmailsSent}</strong>
            </h3>
            <h6 className="m-0 ds-text-muted">
              <div>Total Emails Sent</div>
            </h6>
          </div>
        </div>
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon color={OPENS_COLOR} icon={faCircle}/>
            <div className="fs-large fw-light">{totalOpens} Total Emails Opened</div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon color={CLICKS_COLOR} icon={faCircle}/>
            <div className="fs-large fw-light">{totalClicks} Total Emails With Clicks</div>
          </div>
          {/*
          Remove unsubscribe metric for now, might be added back later after beta
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon color={UNSUBSCRIBES_COLOR} icon={faCircle}/>
            <h4 className="m-0">{totalUnsubscribes} Total Unsubscribes</h4>
          </div> */}
        </div>
      </div>
    </div>
  );
}
